import React, { useMemo, useState } from "react";
import styled from "styled-components";
import toast from "react-hot-toast";

import Modal from "../../../components/modal/Modal";
import ConfirmModal from "../../../components/modal/ConfirmModal";
import Button from "../../../components/button/Button";
import Input from "../../../components/form-item/Input";
import Toggle from "../../../components/form-item/Toggle";
import Avatar from "../../../components/avatar/Avatar";
import { useSearchAdminProjectsQuery } from "../../../app/services/adminAcr";
import {
  useCreateLibrarySectionMutation,
  useUpdateLibrarySectionMutation,
  useDeleteLibrarySectionMutation,
  useGetLibraryRoleCandidatesQuery,
  type LibraryPosition,
  type LibraryRoleInput,
  type LibraryRoleName,
  type LibrarySection,
  type LibraryUserBrief,
} from "../../../app/services/library";

/**
 * ライブラリセクションの作成・権限設定モーダル。
 *
 * notes の SectionSettingsModal のミラー（プルリクエスト保護ルールと
 * 外部公開 is_public を持たない簡素版）:
 * - 基本設定（対象プロジェクト / 閲覧ポジション / アクセス期間）:
 *   master_admin（canManage）のみ
 * - ロール（オーナー / エディター）: master_admin とセクションオーナー
 *
 * 対象プロジェクトは 1 件以上必須。閲覧はセクションの対象プロジェクトの
 * メンバーシップ、編集はロール割り当て（ポジション紐付け or ユーザー直接
 * 指定）で決まる。
 */

const POSITION_OPTIONS: { value: LibraryPosition; label: string }[] = [
  { value: "CLIENT", label: "クライアント" },
  { value: "REVIEWER", label: "レビュアー" },
  { value: "PRODUCER", label: "プロデューサー" },
  { value: "DIRECTOR", label: "ディレクター" },
  { value: "SPECIALIST", label: "スペシャリスト" },
];

const ROLE_DEFS: {
  role: LibraryRoleName;
  label: string;
  hint: string;
}[] = [
  {
    role: "owner",
    label: "オーナー",
    hint: "ロール設定の変更とすべてのファイル操作ができます",
  },
  {
    role: "editor",
    label: "エディター",
    hint: "アップロード / リネーム / 移動 / 削除 / フォルダ作成 / 共有リンクの管理ができます",
  },
];

const FieldLabel = styled.p.attrs({
  className: "heading-13 u-line-height-150",
})`
  margin: 0;
`;

const FieldHint = styled.p.attrs({
  className: "bodytext-11 u-text-light-gray u-line-height-150",
})`
  margin: 0;
`;

const CheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 2px 0;

  input {
    width: 18px;
    height: 18px;
    accent-color: var(--soremo-blue);
    cursor: pointer;
  }
`;

const Chip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 12px;
  background: #eaf6fb;
  color: var(--soremo-blue);

  button {
    display: flex;
    border: none;
    background: transparent;
    cursor: pointer;
    padding: 0;
    color: inherit;

    .material-symbols-rounded {
      font-size: 14px;
    }
  }
`;

const SearchResultButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 6px 8px;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;
  text-align: left;

  .material-symbols-rounded {
    font-size: 16px;
    color: var(--soremo-light-gray);
  }

  @media (hover: hover) {
    &:hover {
      background: #f7f7f7;
    }
  }
`;

/* ロールごとの割り当てブロック（枠付きボックスは utility に該当なし） */
const RoleBox = styled.div.attrs({ className: "u-col u-gap8" })`
  align-items: stretch;
  padding: 12px;
  border: 1px solid var(--soremo-border);
  border-radius: 6px;
  background: #fcfcfc;
`;

interface SelectedProject {
  id: string;
  name: string;
}

/** ロールごとの割り当て状態 */
interface RoleAssignments {
  positions: LibraryPosition[];
  users: LibraryUserBrief[];
}

type RolesState = Record<LibraryRoleName, RoleAssignments>;

const emptyRolesState = (): RolesState => ({
  owner: { positions: [], users: [] },
  editor: { positions: [], users: [] },
});

export interface LibrarySectionSettingsModalProps {
  /** null なら新規作成 */
  section: LibrarySection | null;
  /** master_admin（全設定の編集可）。false ならロール設定のみ */
  canManage: boolean;
  onClose: () => void;
}

const LibrarySectionSettingsModal: React.FC<
  LibrarySectionSettingsModalProps
> = ({ section, canManage, onClose }) => {
  const isNew = section === null;
  const settings = section?.settings;

  const [title, setTitle] = useState(section?.title ?? "");
  const [selectedProjects, setSelectedProjects] = useState<SelectedProject[]>(
    settings?.projects ?? [],
  );
  const [allowedPositions, setAllowedPositions] = useState<LibraryPosition[]>(
    settings?.allowed_positions ?? [],
  );
  const [allowCompleted, setAllowCompleted] = useState(
    settings?.allow_completed ?? true,
  );
  const [roles, setRoles] = useState<RolesState>(() => {
    const state = emptyRolesState();
    for (const entry of settings?.roles ?? []) {
      if (entry.position) {
        state[entry.role].positions.push(entry.position);
      } else if (entry.user) {
        state[entry.role].users.push(entry.user);
      }
    }
    return state;
  });
  const [projectQuery, setProjectQuery] = useState("");
  const [userQuery, setUserQuery] = useState("");
  const [userQueryRole, setUserQueryRole] = useState<LibraryRoleName | null>(
    null,
  );
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  const [createSection, { isLoading: creating }] =
    useCreateLibrarySectionMutation();
  const [updateSection, { isLoading: updating }] =
    useUpdateLibrarySectionMutation();
  const [deleteSection, { isLoading: deleting }] =
    useDeleteLibrarySectionMutation();

  const { data: searchData, isFetching: searching } =
    useSearchAdminProjectsQuery(
      { q: projectQuery, limit: 8 },
      { skip: !canManage || projectQuery.trim() === "" },
    );

  const searchResults = useMemo(() => {
    const selectedIds = new Set(selectedProjects.map((p) => p.id));
    return (searchData?.data?.projects ?? []).filter(
      (p) => !selectedIds.has(p.project_id),
    );
  }, [searchData, selectedProjects]);

  // ロールへのユーザー直接指定の検索（既存セクションのみ。
  // 新規作成時は保存後に設定できる）
  const { data: candidateData, isFetching: searchingUsers } =
    useGetLibraryRoleCandidatesQuery(
      { section_id: section?.id ?? "", q: userQuery },
      { skip: isNew || !userQueryRole || userQuery.trim() === "" },
    );

  const candidates = useMemo(() => {
    if (!userQueryRole) return [];
    const assignedIds = new Set(roles[userQueryRole].users.map((u) => u.id));
    return (candidateData?.data?.users ?? []).filter(
      (u) => !assignedIds.has(u.id),
    );
  }, [candidateData, roles, userQueryRole]);

  const togglePosition = (position: LibraryPosition) => {
    setAllowedPositions((prev) =>
      prev.includes(position)
        ? prev.filter((p) => p !== position)
        : [...prev, position],
    );
  };

  const toggleRolePosition = (role: LibraryRoleName, position: LibraryPosition) =>
    setRoles((prev) => ({
      ...prev,
      [role]: {
        ...prev[role],
        positions: prev[role].positions.includes(position)
          ? prev[role].positions.filter((p) => p !== position)
          : [...prev[role].positions, position],
      },
    }));

  const addRoleUser = (role: LibraryRoleName, user: LibraryUserBrief) => {
    setRoles((prev) => ({
      ...prev,
      [role]: { ...prev[role], users: [...prev[role].users, user] },
    }));
    setUserQuery("");
  };

  const removeRoleUser = (role: LibraryRoleName, userId: number) =>
    setRoles((prev) => ({
      ...prev,
      [role]: {
        ...prev[role],
        users: prev[role].users.filter((u) => u.id !== userId),
      },
    }));

  const buildRolesInput = (): LibraryRoleInput[] => {
    const result: LibraryRoleInput[] = [];
    for (const { role } of ROLE_DEFS) {
      for (const position of roles[role].positions) {
        result.push({ role, position });
      }
      for (const user of roles[role].users) {
        result.push({ role, user_id: user.id });
      }
    }
    return result;
  };

  // 対象プロジェクトは 1 件以上必須（BE でも同じ検証をする）
  const projectsMissing = canManage && selectedProjects.length === 0;

  const handleSave = async () => {
    if (canManage && !title.trim()) {
      toast.error("セクション名を入力してください");
      return;
    }
    if (projectsMissing) {
      toast.error("プロジェクトを 1 つ以上選択してください");
      return;
    }
    try {
      if (isNew) {
        await createSection({
          title: title.trim(),
          project_ids: selectedProjects.map((p) => p.id),
          allowed_positions: allowedPositions,
          allow_completed: allowCompleted,
          roles: buildRolesInput(),
        }).unwrap();
        toast.success("セクションを作成しました");
      } else if (canManage) {
        await updateSection({
          section_id: section.id,
          title: title.trim(),
          project_ids: selectedProjects.map((p) => p.id),
          allowed_positions: allowedPositions,
          allow_completed: allowCompleted,
          roles: buildRolesInput(),
        }).unwrap();
        toast.success("セクション設定を保存しました");
      } else {
        // セクションオーナーはロール設定のみ変更できる
        await updateSection({
          section_id: section.id,
          roles: buildRolesInput(),
        }).unwrap();
        toast.success("ロール設定を保存しました");
      }
      onClose();
    } catch {
      toast.error("保存に失敗しました");
    }
  };

  const handleDelete = async () => {
    if (!section) return;
    try {
      await deleteSection({ section_id: section.id }).unwrap();
      toast.success("セクションを削除しました");
      onClose();
    } catch {
      toast.error("削除に失敗しました");
    }
  };

  if (confirmingDelete && section) {
    return (
      <ConfirmModal
        message={`セクション「${section.title}」を削除しますか？\n配下のフォルダ・ファイルも表示されなくなります。`}
        confirmLabel="削除"
        loading={deleting}
        onConfirm={handleDelete}
        onCancel={() => setConfirmingDelete(false)}
      />
    );
  }

  return (
    <Modal
      title={isNew ? "セクションを追加" : "セクション設定"}
      titleSize="medium"
      size="default"
      onClose={onClose}
      primaryAction={{
        label: isNew ? "作成" : "保存",
        onClick: handleSave,
        loading: creating || updating,
        disabled: projectsMissing,
      }}
      secondaryAction={{
        label: "キャンセル",
        onClick: onClose,
      }}
    >
      <div className="u-col u-gap16">
        {canManage && (
          <>
            <Input
              label="セクション名"
              option="required"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="例: 素材ライブラリ"
              maxlength={255}
            />

            <div className="u-col u-gap8">
              <FieldLabel>対象プロジェクト</FieldLabel>
              <div className="u-col u-gap8">
                {selectedProjects.length > 0 && (
                  <div className="u-row u-gap4" style={{ flexWrap: "wrap" }}>
                    {selectedProjects.map((p) => (
                      <Chip key={p.id} className="bodytext-11">
                        {p.name}
                        <button
                          aria-label={`${p.name} を外す`}
                          onClick={() =>
                            setSelectedProjects((prev) =>
                              prev.filter((sp) => sp.id !== p.id),
                            )
                          }
                        >
                          <span className="material-symbols-rounded">
                            close
                          </span>
                        </button>
                      </Chip>
                    ))}
                  </div>
                )}
                <Input
                  type="search"
                  value={projectQuery}
                  onChange={(e) => setProjectQuery(e.target.value)}
                  placeholder="プロジェクト名で検索..."
                />
                {searching && <FieldHint>検索中...</FieldHint>}
                {searchResults.map((p) => (
                  <SearchResultButton
                    key={p.project_id}
                    onClick={() => {
                      setSelectedProjects((prev) => [
                        ...prev,
                        { id: p.project_id, name: p.name },
                      ]);
                      setProjectQuery("");
                    }}
                  >
                    <span className="material-symbols-rounded">add</span>
                    <span className="bodytext-13">{p.name}</span>
                  </SearchResultButton>
                ))}
                {selectedProjects.length === 0 &&
                  projectQuery.trim() === "" && (
                    <FieldHint>
                      プロジェクトを 1 つ以上選択してください
                    </FieldHint>
                  )}
              </div>
            </div>

            <div className="u-col u-gap4">
              <FieldLabel>閲覧できるポジション</FieldLabel>
              <FieldHint>
                ポジションが未選択の場合は対象プロジェクトの全メンバーが
                閲覧できます
              </FieldHint>
              {POSITION_OPTIONS.map((opt) => (
                <CheckRow key={`view-${opt.value}`}>
                  <input
                    type="checkbox"
                    checked={allowedPositions.includes(opt.value)}
                    onChange={() => togglePosition(opt.value)}
                  />
                  <span className="bodytext-13">{opt.label}</span>
                </CheckRow>
              ))}
            </div>

            <div className="u-col u-gap4">
              <FieldLabel>アクセス期間</FieldLabel>
              <Toggle
                value={allowCompleted}
                onClick={() => setAllowCompleted((v) => !v)}
              >
                <span className="bodytext-13">
                  {allowCompleted
                    ? "プロジェクト完了後もアクセス可"
                    : "プロジェクト進行中のみアクセス可"}
                </span>
              </Toggle>
            </div>
          </>
        )}

        <div className="u-col u-gap8">
          <FieldLabel>ロール</FieldLabel>
          <FieldHint>
            編集できるメンバーを設定します。ポジション紐付けとユーザー直接
            指定のどちらでも割り当てられます。未設定の場合は管理者のみ
            編集できます（閲覧は対象プロジェクトの全メンバー）。
          </FieldHint>
          {ROLE_DEFS.map(({ role, label, hint }) => (
            <RoleBox key={role}>
              <div className="u-col u-gap4">
                <span className="heading-13 u-line-height-150">{label}</span>
                <FieldHint>{hint}</FieldHint>
              </div>
              <div className="u-row u-gap4" style={{ flexWrap: "wrap" }}>
                {POSITION_OPTIONS.map((opt) => (
                  <CheckRow key={`${role}-${opt.value}`}>
                    <input
                      type="checkbox"
                      checked={roles[role].positions.includes(opt.value)}
                      onChange={() => toggleRolePosition(role, opt.value)}
                    />
                    <span className="bodytext-13">{opt.label}</span>
                  </CheckRow>
                ))}
              </div>
              {roles[role].users.length > 0 && (
                <div className="u-row u-gap4" style={{ flexWrap: "wrap" }}>
                  {roles[role].users.map((user) => (
                    <Chip key={user.id} className="bodytext-11">
                      {user.display_name}
                      <button
                        aria-label={`${user.display_name} を外す`}
                        onClick={() => removeRoleUser(role, user.id)}
                      >
                        <span className="material-symbols-rounded">close</span>
                      </button>
                    </Chip>
                  ))}
                </div>
              )}
              {isNew ? (
                <FieldHint>
                  ユーザー直接指定はセクション作成後に設定できます
                </FieldHint>
              ) : (
                <>
                  <Input
                    type="search"
                    value={userQueryRole === role ? userQuery : ""}
                    onChange={(e) => {
                      setUserQueryRole(role);
                      setUserQuery(e.target.value);
                    }}
                    placeholder="表示名 / ユーザー名 / メールで検索して直接指定..."
                  />
                  {userQueryRole === role && searchingUsers && (
                    <FieldHint>検索中...</FieldHint>
                  )}
                  {userQueryRole === role &&
                    candidates.map((user) => (
                      <SearchResultButton
                        key={user.id}
                        onClick={() => addRoleUser(role, user)}
                      >
                        <Avatar size={32} src={user.avatar ?? undefined} />
                        <span className="bodytext-13">{user.display_name}</span>
                        {user.positions.length > 0 && (
                          <span className="bodytext-11 u-text-light-gray">
                            {user.positions.join(" / ")}
                          </span>
                        )}
                      </SearchResultButton>
                    ))}
                </>
              )}
            </RoleBox>
          ))}
        </div>

        {!isNew && canManage && (
          <div className="u-row-end">
            <Button
              size="small"
              theme="secondary"
              variant="text"
              leftIcon="delete"
              onClick={() => setConfirmingDelete(true)}
            >
              セクションを削除
            </Button>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default LibrarySectionSettingsModal;
